import Vue from 'vue';
import Vuex from 'vuex';
import APISite from '@/api/Site';
import APIUser from '@/api/User';
import APILogin from '@/api/Login';

const properties = {
  site: {
    storage: 'permanent',
    def: null,
  },

  site_data: {
    storage: 'permanent',
    serialize: true,
    def: null,
  },

  centro: { // Centro ID
    storage: 'permanent',
    def: null,
  },

  root_site: {
    storage: 'permanent',
    def: null,
  },

  token: {
    storage: 'permanent',
    key: 'vue-authenticate.vueauth_token',
    def: null,
  },

  session_cookie: { // Set to true when using session cookie
    storage: 'permanent',
    def: false,
  },

  user: {
    storage: 'permanent',
    serialize: true,
    def: {},
  },
};

// Create reverse tree for storage keys to UserSession key
const storage_to_us = {};
for (const [key, props] of Object.entries(properties))
  storage_to_us[props.key || key] = key;
//

const local = window.localStorage;
const session = window.sessionStorage;

function handle_browser_store(key, value) {
  const props = properties[key];
  const storage = props.storage == 'permanent' ? local : session;
  if (value === undefined)
    storage.removeItem(props.key||key);
  else
    storage.setItem(props.key||key, (props.serialize ? JSON.stringify(value) : value));
}

function default_state() {
  const st = {
    installed: false,
  };
  for (const [key, props] of Object.entries(properties))
    st[key] = props.def || null;
  return st;
}

let user_session_installed = false;

const state = default_state();

const getters = {};

const actions = {
  set_site({ commit }, site_data) {
    commit('set_site', site_data.id);
    commit('set_site_data', site_data);
  },

  set_root_site({ commit }, root_site) {
    commit('set_root_site', root_site);
  },

  set_centro({ commit }, centro_id) {
    commit('set_centro', centro_id);
  },

  set_token({ commit }, token) {
    commit('set_token', token);
  },

  set_session_cookie({ commit }, using_session_cookie) {
    commit('set_session_cookie', using_session_cookie);
  },

  set_user({ commit }, user) {
    commit('set_user', user ? {...user} : undefined);
  },

  async query_user_data({ commit }) {
    const user_data = await APIUser.current();
    commit('set_user', user_data ? {...user_data} : undefined);
  },

  async purge_session({ commit, state }) {
    let promise = null;
    if (state.session_cookie) {
      let promise = APILogin.logout();
    }

    commit('purge_session');

    if (promise !== null)
      await promise;
  },

  install({ commit, state }) {
    if (state.installed)
      return;
    user_session_installed = true;

    window.addEventListener('storage', (event) => commit('handle_tab_event', event));

    commit('load_data_from_storage');
  },

  async update_site_data({ commit, state }) {
    const sid = state.site_data.id;
    const site_data = await APISite.get(sid);
    commit('set_site', site_data.id);
    commit('set_site_data', site_data);
  },

  async query_site_data({ commit, state }, site_id) {
    const site_data = await APISite.get(site_id);
    commit('set_site', site_data.id);
    commit('set_site_data', site_data);
  },
};

const mutations = {
  set_site(state, siteid) {
    state.site = siteid;
    handle_browser_store('site', siteid);
  },

  set_site_data(state, site_data) {
    state.site_data = site_data;
    handle_browser_store('site_data', site_data);
  },

  set_root_site(state, root_site) {
    state.root_site = root_site;
    handle_browser_store('root_site', root_site);
  },

  set_centro(state, centro_id) {
    state.centro = centro_id;
    handle_browser_store('centro', centro_id);
  },

  set_token(state, token) {
    state.token = token;
    handle_browser_store('token', token);
  },

  set_session_cookie(state, using_session_cookie) {
    const val = !!using_session_cookie;
    state.session_cookie = val;
    handle_browser_store('session_cookie', val);
  },

  set_user(state, user) {
    state.user = user;
    handle_browser_store('user', user);
  },

  purge_session(state) {
    const original = default_state();
    for (const [key, value] of Object.entries(original)) {
      if (properties[key] !== undefined) {
        handle_browser_store(key, undefined);
        state[key] = value;
      }
    }
  },

  load_data_from_storage(state) {
    let raw;
    for (const [key, props] of Object.entries(properties)) {
      const storage = props.storage == 'permanent' ? local : session;
      if (storage.getItem(props.key||key)) {
        raw = storage.getItem(props.key||key);
        state[key] = props.serialize ? JSON.parse(raw) : raw;
      }
    }

    state.installed = true;
  },

  handle_tab_event(state, event) {
    // console.warn("handle_tab_event", event);
    const key = storage_to_us[event.key];
    if (key) {
      const props = properties[key];
      const value = event.newValue;
      state[key] = props.serialize ? JSON.parse(value) : value;
    }
  },
};

Vue.use(Vuex);

export default {
  namespaced: true,
  state: state,
  actions: actions,
  getters: getters,
  mutations: mutations,
};
